/**
 * **The app's loading policy, as one decision.** Owner, 2 September 2026: "ma nie być efektu
 * skoków, ma być czyściutko."
 *
 * | situation | what the trainer sees |
 * |---|---|
 * | the answer arrives inside the grace window | the content, and no skeleton at all |
 * | the answer takes longer | the skeleton, for at least its minimum life |
 * | the answer lands while the skeleton is young | the skeleton, until that life is up, and the CONTENT waits with it |
 *
 * Two numbers, and each of them buys something specific:
 *
 * · **The grace window** is what stops a skeleton FLASHING. A warm cache and a fast connection
 *   answer in tens of milliseconds; a skeleton drawn and removed inside two frames is a flicker,
 *   which reads worse than the wait it was covering.
 * · **The minimum life** is what stops it flashing at the OTHER end. A skeleton that appears at
 *   151ms and is replaced at 160 has the same problem the grace window solves, from the far side.
 *   Note that this holds the CONTENT back too, not only the bars: `useSkeletonGate` turns it into a
 *   `waiting` flag and the surface forks its whole wait branch on that. Until 21 September 2026
 *   every call site forked on its own read instead, and this row of the table was a statement about
 *   nothing the trainer could see.
 *
 * ── What this is NOT ──────────────────────────────────────────────────────────────────────────
 * It is not a `setTimeout` masking a jump. The owner ruled those out in the same breath, and the
 * distinction is worth stating because both use a timer: a mask delays the CONTENT so a
 * measurement cannot see it move. This delays only the SKELETON, and the card it lives in holds
 * the same box in every state, so nothing this gate switches between changes a height. A card
 * that would move when its skeleton appears is a card whose reserve is wrong, and no timer fixes
 * that.
 *
 * ── Pure, and therefore testable ──────────────────────────────────────────────────────────────
 * The clock arrives as a parameter and the result says when to look again, so the hook that owns
 * the timer contains no policy and this file contains no timer. `model`-shaped: no React, no DOM.
 */

/** How long an answer has to take before a skeleton is worth drawing. */
export const SKELETON_GRACE_MS = 150;

/** How long a skeleton stays once it is on screen, however quickly the answer follows. */
export const SKELETON_MIN_LIFE_MS = 300;

export interface LoadingGateInput {
  /** Whether the reads behind this surface are still open. */
  readonly pending: boolean;
  /** When the current wait began, or `null` when nothing is waiting. */
  readonly pendingSince: number | null;
  /** When the skeleton went on screen, or `null` while it is not showing. */
  readonly shownAt: number | null;
  readonly now: number;
}

export interface LoadingGateVerdict {
  /** Whether the skeleton belongs on screen right now. */
  readonly visible: boolean;
  /**
   * When the answer could change without any new input — the moment the grace window closes, or
   * the moment the minimum life is up. `null` means nothing is pending on the clock.
   */
  readonly recheckAt: number | null;
}

/**
 * The whole policy, for one surface, at one instant.
 *
 * @param input the wait's two timestamps and the clock. `pendingSince` and `shownAt` are the
 *   caller's memory of the same wait; both are milliseconds on one monotonic scale.
 */
export function loadingGate(input: LoadingGateInput): LoadingGateVerdict {
  const { pending, pendingSince, shownAt, now } = input;

  if (shownAt !== null) {
    // On screen already. It stays until the reads are done AND it has had its minimum life —
    // whichever of those two is later.
    const expiresAt = shownAt + SKELETON_MIN_LIFE_MS;
    if (pending) return { visible: true, recheckAt: null };
    if (now < expiresAt) return { visible: true, recheckAt: expiresAt };
    return { visible: false, recheckAt: null };
  }

  if (!pending || pendingSince === null) return { visible: false, recheckAt: null };

  const showAt = pendingSince + SKELETON_GRACE_MS;
  if (now < showAt) return { visible: false, recheckAt: showAt };
  return { visible: true, recheckAt: null };
}
